<template>
  <div class="fp-calendar" id="fp-calendar">
    <div class="calendar-header">
      <h4 class="calendar-subtitle uppercase-text">{{ $lang.messages.monthlyCalendar }}</h4>
      
      <img @click="subtractMonth" src="@/assets/images/payments/arrow_left_active.png" class="arrow" alt="left-arrow" />
      <div>{{ month + ' - ' + year }}</div>
      <img @click="addMonth" src="@/assets/images/payments/arrow_right_active.png" class="arrow" alt="right-arrow" />
      
      <button type="button" class="today-btn" @click="goToToday()">
        {{ $lang.messages.today }}
      </button>
      
      <div class="export-calendar-menu" v-click-outside="hideActionMenu">
        <button type="button" class="filter-select-btn" @click="exportOpened = !exportOpened">
          <span>{{ $lang.messages.exportCalendar }}</span>
          <img class="select-icon" src='@/assets/images/arrow_down.png' :class="{'select-icon-rotate': exportOpened}" />
        </button>
        <div class="action-menu" :class="!exportOpened ? 'select-menu-not-collapsed' : 'select-menu-collapsed'" >
          <div @click="openCalendarDialog">
            {{ $lang.messages.iCalLink }}
          </div>
          <div @click="print">
            {{ $lang.messages.print }}
          </div>
        </div>
      </div>
    </div>

    <f-p-export-calendar
      :modalTitle="'Export Calendar'"
      :childrenList="childrenList"
      ref="exportCalendar">
    </f-p-export-calendar>
    
    <div id="calendar">
      <div class="calendar-title">{{ $lang.messages.calendar }} {{ $lang.messages.forLabel }} {{ fullMonth }}</div>
  
      <div class="weekdays">
        <div
            v-for="day in days"
            :key="day"
            class="day"
        >
          {{ day }}
        </div>
      </div>
  
      <div class="dates">
        <div
            v-for="(day, indexPrevDays) in previousDays"
            :key="`prevDays-${indexPrevDays}`"
            class="day"
            :class="{'past-day' : createDate(day, previousMonth, previousYear) < today}"
        >
          <div class="date" :class="{'first-date' : day === 1}">{{ day }}</div>
  
          <calendar-child-visitation
            v-if="showVisitations"
            :visitations="getVisitations(day, previousMonth, previousYear).visitations"
            :date="getVisitations(day, previousMonth, previousYear).date">
          </calendar-child-visitation>
      
          <calendar-single-event
            v-if="showEvents"
            :childrenList="childrenList"
            :events="getEvents(day, previousMonth, previousYear).events"
            :event="getEvents(day, previousMonth, previousYear).firstEvent">
          </calendar-single-event>
        </div>
    
        <div
          v-for="(day, indexDayMonth) in daysInMonth"
          :key="`months-${indexDayMonth}`"
          class="day"
          :class="{'past-day' : createDate(day, month, year) < today}"
        >
          <div class="month" v-if="day === 1">{{ month }}</div>
          <div
            class="date"
            :class="{
              'current-day': day == currentDate && month == currentMonth && year == currentYear,
              'first-date' : day === 1
            }"
          >
            {{ day }}
          </div>
      
          <calendar-child-visitation
            v-if="showVisitations"
            :visitations="getVisitations(day, month, year).visitations"
            :date="getVisitations(day, month, year).date">
          </calendar-child-visitation>
      
          <calendar-single-event
            v-if="showEvents"
            :childrenList="childrenList"
            :events="getEvents(day, month, year).events"
            :event="getEvents(day, month, year).firstEvent">
          </calendar-single-event>
        </div>
    
        <div
          v-for="(day, index) in (42 - firstDayOfMonth - daysInMonth)"
          :key="`pastDay-${index}`"
          class="day"
          :class="{'past-day' : createDate(day, nextMonth, nextYear) < today}"
        >
          <div class="month" v-if="day === 1">{{ nextMonth }}</div>
          <div class="date" :class="{'first-date' : day === 1}">{{ day }}</div>
  
          <calendar-child-visitation
            v-if="showVisitations"
            :visitations="getVisitations(day, nextMonth, nextYear).visitations"
            :date="getVisitations(day, nextMonth, nextYear).date">
          </calendar-child-visitation>
      
          <calendar-single-event
            v-if="showEvents"
            :childrenList="childrenList"
            :events="getEvents(day, nextMonth, nextYear).events"
            :event="getEvents(day, nextMonth, nextYear).firstEvent">
          </calendar-single-event>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    import CalendarSingleEvent from "./CalendarSingleEvent";
    import CalendarChildVisitation from "./CalendarChildVisitation";
    import {addMonths, addYears, format, getDaysInMonth, parseISO, subDays, subMonths, subYears} from 'date-fns';
    import FPExportCalendar from "@/components/common/modals/FPExportCalendar";
    import vClickOutside from 'v-click-outside'

    export default {
    name: "FPCalendar",
    components: {
      FPExportCalendar,
      CalendarSingleEvent,
      CalendarChildVisitation
    },
    props: {
      childrenList: {
        type: Array,
        required: true,
        default: () => []
      },
      eventsList: {
        type: Array,
        required: true,
        default: () => []
      },
      childVisitationsList: {
        type: Array,
        required: true,
        default: () => []
      },
      paymentsList: {
        type: Array,
        required: true,
        default: () => []
      },
      showEvents: {
        type: Boolean,
        required: true,
        default: () => true
      },
      showVisitations: {
        type: Boolean,
        required: true,
        default: () => true
      },
      firstDayOfWeek: {
        type: Number,
        required: true,
        default: () => 0
      },
      dateParam: {
        type: String,
        required: true,
        default: () => ''
      }
    },
    data() {
      return {
        dateContext: new Date(),
        days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        exportOpened: false,
      }
    },
    directives: {
      clickOutside: vClickOutside.directive
    },
    computed: {
      year: function () {
        return format(this.dateContext, 'yyyy')
      },
      month: function () {
        return format(this.dateContext, 'MMM');
      },
      daysInMonth: function () {
        return getDaysInMonth(this.dateContext);
      },
      currentDate: function () {
        return format(this.dateContext, 'dd')
      },
      currentMonth: function () {
        return format(this.today, 'MMM');
      },
      currentYear: function () {
        return format(this.today, 'yyyy');
      },
      firstDayOfMonth: function () {
        let firstDay = subDays(this.dateContext, this.currentDate - 1 + this.firstDayOfWeek);
        return firstDay.getDay();
      },
      initialMonth: function () {
        return format(this.dateContext, 'MMM');
      },
      initialYear: function () {
        return format(this.dateContext, 'yyyy');
      },
      previousYear: function() {
        if (this.month === 'Jan') {
          let date = this.clone(this.dateContext);
          return format(subYears(date, 1), 'yyyy')
        }
        return this.year
      },
      previousMonth: function() {
        let date = this.clone(this.dateContext);
        return format(subMonths(date, 1), 'MMM')
      },
      daysInPreviousMonth: function() {
        let date = this.createDate('1', this.previousMonth, this.previousYear);
        return getDaysInMonth(date)
      },
      previousDays: function() {
        let days = [];
        for (let i = this.daysInPreviousMonth - this.firstDayOfMonth + 1; i <= this.daysInPreviousMonth; i++) {
          days.push(i)
        }
        return days
      },
      nextYear: function() {
        if (this.month === 'Dec') {
          let date = this.clone(this.dateContext);
          return format(addYears(date, 1), 'yyyy')
        }
        return this.year
      },
      nextMonth: function() {
        let date = this.clone(this.dateContext);
        return format(addMonths(date, 1), 'MMM')
      },
      startDate: function() {
        if (this.firstDayOfMonth === 0) {
          return this.createDate('1', this.initialMonth, this.initialYear)
        }
        return this.createDate(this.daysInPreviousMonth - this.firstDayOfMonth + 1, this.previousMonth, this.previousYear)
      },
      endDate: function() {
        return this.createDate(42 - this.firstDayOfMonth - this.daysInMonth, this.nextMonth, this.nextYear)
      },
      fullMonth: function() {
        return format(this.today, 'MMMM');
      },
      today: function() {
        let todayTemp = new Date();
        return new Date(todayTemp.getFullYear(), todayTemp.getMonth(), todayTemp.getDate(), 0, 0, 0);
      },
    },
    mounted() {
      this.days = this.sortByDayOfWeek(this.days, this.firstDayOfWeek);
      if (this.dateParam !== '') {
        this.dateContext = new Date(this.dateParam)
      }
    },
    methods: {
      hideActionMenu() {
        this.exportOpened = false;
      },
      openCalendarDialog() {
        this.exportOpened = !this.exportOpened;
        this.$refs.exportCalendar.show();
      },
      addMonth: function () {
        this.dateContext = addMonths(this.dateContext, 1);
        this.$emit("loadData")
      },
      subtractMonth: function () {
        this.dateContext = subMonths(this.dateContext, 1);
        this.$emit("loadData")
      },
      createDate(dateParam, monthParam, yearParam) {
        return new Date(dateParam + ' ' +  monthParam + ', ' + yearParam)
      },
      sortByDayOfWeek(arr, dayOfWeek) {
        return arr.slice(dayOfWeek).concat(arr.slice(0, dayOfWeek));
      },
      goToToday() {
        this.dateContext = new Date();
        this.$emit("loadData")
      },
      getDate(date) {
        if (typeof date === 'string') {
            return parseInt(format(parseISO(date), 'd'))
        }
        return parseInt(format(date, 'd'))
      },
      getMonth(date) {
          if (typeof date === 'string') {
              return format(parseISO(date), 'MMM')
          }
        return format(date, 'MMM')
      },
      clone(obj) {
        let copy;

        if (null == obj || "object" != typeof obj) return obj;

        if (obj instanceof Date) {
          copy = new Date();
          copy.setTime(obj.getTime());
          return copy;
        }

        if (obj instanceof Array) {
          copy = [];
          for (let i = 0, len = obj.length; i < len; i++) {
            copy[i] = this.clone(obj[i]);
          }
          return copy;
        }

        if (obj instanceof Object) {
          copy = {};
          for (let attr in obj) {
            if (obj.hasOwnProperty(attr)) copy[attr] = this.clone(obj[attr]);
          }
          return copy;
        }
      },
      print() {
        this.exportOpened = !this.exportOpened;
        setTimeout(function(){
          window.print();
        }, 300);
      },
      getVisitations(day, month, year) {
        let date = format(this.createDate(day, month, year), 'yyyy-MM-dd');
        let visitations = this.childVisitationsList.find(visitationIt => visitationIt.date === date)
  
        let data = {
          'visitations' : [],
          'date' : date
        }
  
        if (visitations) {
          data.visitations = visitations.cases
        }
        
        return data
      },
      getEvents(day, month, year) {
        let date = format(this.createDate(day, month, year), 'yyyy-MM-dd');
        let events = this.eventsList.filter(eventIt => format(new Date(eventIt.startDateTime), 'yyyy-MM-dd') === date)
        
        events.forEach(event => {
          event.randomEventGuid = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10)
        })
        
        let data = {
          'events' : events,
          'firstEvent' : events.length > 0 ? events[0] : {}
        }
    
        return data
      }
    }
  }
</script>
